import { observer } from "mobx-react-lite";
import AppDialog from "@/components/common/AppDialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowRight } from "lucide-react";
import { useTran } from "@/hooks";
import { usePrefillStore } from "../../hooks/useDataEntryStore";
import { applyPrefillSuggestions } from "../../operations/prefill.ops";

const PrefillModal = observer(() => {
  const { t } = useTran();
  const { showModal, setShowModal, suggestions } = usePrefillStore();

  return (
    <AppDialog
      open={showModal}
      onOpenChange={setShowModal}
      title={t("Prefill Details")}
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={() => setShowModal(false)}>
            {t("Cancel")}
          </Button>
          <Button onClick={applyPrefillSuggestions}>
            {t("Apply All Values")}
          </Button>
        </>
      }
    >
      <div className="space-y-4 max-h-[60vh] overflow-y-auto">
        <p className="text-sm text-muted-foreground">
          {t(
            "The following data will be copied from matching disclosures in other frameworks:",
          )}
        </p>

        <div className="space-y-3">
          {suggestions.map((match, index) => (
            <div
              key={index}
              className="border border-border rounded-lg p-3 space-y-2"
            >
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="outline">{match.sourceFramework}</Badge>
                <span className="text-sm font-medium">
                  {match.sourceDisclosureCode}
                </span>
                <ArrowRight className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">
                  {match.sourceDisclosureTitle}
                </span>
              </div>

              <div className="text-xs text-muted-foreground">
                {t("{{count}} entry(s)", { count: match.entries.length })}
              </div>

              {match.entries.slice(0, 2).map((entry, i) => (
                <div
                  key={i}
                  className="text-sm bg-muted/50 rounded px-2 py-1 flex items-center gap-2"
                >
                  {entry.label && (
                    <span className="text-muted-foreground">{entry.label}</span>
                  )}
                  <span className="font-medium tabular-nums">
                    {entry.value ?? "—"}
                  </span>
                  {entry.unit && (
                    <span className="text-muted-foreground text-xs">
                      {entry.unit}
                    </span>
                  )}
                </div>
              ))}

              {match.entries.length > 2 && (
                <div className="text-xs text-muted-foreground">
                  {t("+ {{count}} more entries", {
                    count: match.entries.length - 2,
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </AppDialog>
  );
});

export default PrefillModal;
